import { Button, Flex, Icon, Text } from "@chakra-ui/react";
import { Tooltip } from "../../common/components/Tooltip";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaMagic } from "react-icons/fa";
import { type NeedDto, type NeedPatchDto } from "../../autogen/bff-api";
import { useEditor } from "../../common/editing/save-coordinator";
import { tiptapToPlainText } from "../../common/input/TipTap/tiptapToPlainText";
import { AskFurtherButton } from "../../common/agent/AskFurtherButton";
import { useNeedPriorityLabel } from "./useNeedPriorityLabel";
import { useNeedTypeSuggestion, type NeedTypeSuggestion } from "./useNeedTypeSuggestion";

export const NeedTypeAiSuggestion = ({ needId }: { needId: string }) => {
  const { t } = useTranslation();
  const { state, updateField } = useEditor<NeedDto, NeedPatchDto>(needId);
  const typeSuggestion = useNeedTypeSuggestion(needId);
  const priorityLabel = useNeedPriorityLabel();
  const [suggestion, setSuggestion] = useState<NeedTypeSuggestion | null>(null);

  const title = state.title?.trim() ?? "";
  const description = useMemo(() => tiptapToPlainText(state.description), [state.description]);
  const hasContext = title !== "" || description !== "";

  const handleSuggest = async () => {
    setSuggestion(null);
    const suggested = await typeSuggestion.suggest(title, description);
    if (!suggested) return;
    updateField({ procurementType: suggested.procurementType });
    if (!state.priority) updateField({ priority: suggested.priority });
    setSuggestion(suggested);
  };

  const suggestionAsShown = (shown: NeedTypeSuggestion) => ({
    source: t("Suggested type and priority"),
    subject: title || undefined,
    text: [`${t("Type")}: ${t(shown.procurementType)}`, `${t("Priority")}: ${priorityLabel(shown.priority)}`, shown.reasoning]
      .filter(Boolean)
      .join("\n"),
  });

  return (
    <Flex direction="column" align="flex-start" gap="2" mt="3">
      <Tooltip label={t("Add a title or description first")} isDisabled={hasContext} shouldWrapChildren>
        <Button
          size="xs"
          variant="outline"
          colorScheme="teal"
          leftIcon={<Icon as={FaMagic} />}
          onClick={handleSuggest}
          isLoading={typeSuggestion.isLoading}
          loadingText={t("Analyzing…")}
          isDisabled={!hasContext}
        >
          {t("Suggest type & priority with AI")}
        </Button>
      </Tooltip>
      {suggestion?.reasoning && (
        <Text fontSize="xs" color="smMuted">
          {suggestion.reasoning}
        </Text>
      )}
      {suggestion && <AskFurtherButton result={() => suggestionAsShown(suggestion)} />}
      {typeSuggestion.hasError && (
        <Text fontSize="xs" color="smError">
          {t("Couldn't generate suggestions. Please try again.")}
        </Text>
      )}
    </Flex>
  );
};
